import { useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Ticket types that can be subscribed to
export type TicketType = 'INCIDENT' | 'PROBLEM' | 'CHANGE' | 'CASE' | 'ALERT' | 'SERVICE_REQUEST';

// SSE event payload structure
export interface TicketEvent {
  eventType: string;
  ticketType: TicketType;
  ticketId: string;
  ticketNumber: string;
  timestamp: string;
  customerId?: string;
  engagementId?: string;
  shortDescription?: string;
  priority?: string;
  state?: string;
}

interface UseTicketEventsOptions {
  /**
   * Ticket types to subscribe to. If not specified, subscribes to all types.
   */
  ticketTypes?: TicketType[];
  /**
   * Whether the SSE connection should be enabled. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Callback function called when a ticket event is received.
   * Use this to show notifications or perform other actions.
   */
  onEvent?: (event: TicketEvent) => void;
}

// Map ticket types to their query keys (including dashboard keys for real-time updates)
const TICKET_TYPE_TO_QUERY_KEYS: Record<TicketType, string[]> = {
  INCIDENT: ['incidents', 'incident-stats', 'incident-dashboard'],
  PROBLEM: ['problems', 'problem-stats', 'problem-dashboard'],
  CHANGE: ['changes', 'change-stats', 'change-dashboard'],
  CASE: ['cases', 'case-stats', 'customer-wise-dashboard'],
  ALERT: ['alerts', 'alert-stats'],
  SERVICE_REQUEST: ['service-requests', 'service-request-stats', 'service-request-dashboard'],
};

// API base URL (same as in api.ts)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api/v1';

/**
 * Hook for subscribing to real-time ticket events via Server-Sent Events (SSE).
 * When a ticket is created/updated, the relevant React Query cache is invalidated,
 * causing the list to automatically refresh.
 *
 * @example
 * // Subscribe to incident events only
 * useTicketEvents({ ticketTypes: ['INCIDENT'] });
 *
 * @example
 * // Subscribe to all ticket events
 * useTicketEvents();
 */
export function useTicketEvents(options: UseTicketEventsOptions = {}) {
  const { ticketTypes, enabled = true, onEvent } = options;
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const isConnectingRef = useRef(false);
  const maxReconnectAttempts = 10;

  // Store callback in ref to avoid re-creating SSE connection when callback changes
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // Serialize ticketTypes to a stable string for dependency comparison
  const ticketTypesKey = useMemo(
    () => (ticketTypes ? ticketTypes.sort().join(',') : 'ALL'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketTypes?.join(',')]
  );

  useEffect(() => {
    // Don't connect if disabled
    if (!enabled) return;

    // Track if component is still mounted
    let isMounted = true;

    // Add delay to handle React Strict Mode double-mounting
    // React Strict Mode unmounts after ~50ms, so 500ms should be safe
    const connectionDelay = setTimeout(() => {
      // Check if still mounted and no existing connection
      if (!isMounted) {
        console.log('[SSE] Component unmounted, skipping connection');
        return;
      }
      if (eventSourceRef.current) {
        console.log('[SSE] Connection already exists, skipping');
        return;
      }
      if (isConnectingRef.current) {
        console.log('[SSE] Already connecting, skipping');
        return;
      }

      connect();
    }, 500);

    const connect = () => {
      // Get the access token
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.log('[SSE] No access token, skipping connection');
        return;
      }

      isConnectingRef.current = true;

      // Build the SSE URL
      const params = new URLSearchParams({ token });
      if (ticketTypesKey !== 'ALL') {
        params.set('ticketTypes', ticketTypesKey);
      }
      const sseUrl = `${API_BASE_URL}/ticket-events/sse?${params.toString()}`;

      console.log('[SSE] Connecting to ticket events stream...');
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // Handle connection open
      eventSource.addEventListener('connected', () => {
        console.log('[SSE] Connected to ticket events stream');
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
      });

      // Handle ticket update events
      eventSource.addEventListener('ticket-update', (event) => {
        try {
          const ticketEvent: TicketEvent = JSON.parse(event.data);
          console.log(`[SSE] Received ${ticketEvent.eventType} for ${ticketEvent.ticketType}`);

          // Call the onEvent callback if provided
          if (onEventRef.current) {
            onEventRef.current(ticketEvent);
          }

          // Get the query keys to invalidate for this ticket type
          const queryKeys = TICKET_TYPE_TO_QUERY_KEYS[ticketEvent.ticketType];
          if (queryKeys) {
            queryKeys.forEach((queryKey) => {
              // Invalidate queries - only refetch if they're currently mounted (active)
              queryClient.invalidateQueries({
                queryKey: [queryKey],
                refetchType: 'active',
              });
            });
          }

          // Also invalidate 'tickets' query if it exists (for unified ticket view)
          queryClient.invalidateQueries({
            queryKey: ['tickets'],
            refetchType: 'active',
          });

          // Invalidate dashboard queries
          queryClient.invalidateQueries({
            queryKey: ['dashboard'],
            refetchType: 'active',
          });
          queryClient.invalidateQueries({
            queryKey: ['kpi-dashboard-charts'],
            refetchType: 'active',
          });
          queryClient.invalidateQueries({
            queryKey: ['kpi-dashboard-filters'],
            refetchType: 'active',
          });

          queryClient.invalidateQueries({
            queryKey: ['servicenow-awaiting-activity'],
            refetchType: 'active',
          });
        } catch (err) {
          console.error('[SSE] Error parsing ticket event:', err);
        }
      });

      eventSource.addEventListener('servicenow-awaiting-activity', () => {
        queryClient.invalidateQueries({
          queryKey: ['servicenow-awaiting-activity'],
          refetchType: 'active',
        });
      });

      // Handle errors and reconnection
      eventSource.onerror = () => {
        console.error('[SSE] Connection error');
        isConnectingRef.current = false;

        // Close the errored connection
        eventSource.close();
        eventSourceRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          console.error('[SSE] Max reconnection attempts reached, giving up');
        }
      };
    };

    // Note: connect() is called via the setTimeout above, not directly here
    // This handles React Strict Mode double-mounting

    // Cleanup function
    return () => {
      isMounted = false;

      // Clear connection delay
      clearTimeout(connectionDelay);

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close the SSE connection
      if (eventSourceRef.current) {
        console.log('[SSE] Disconnecting from ticket events stream');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      isConnectingRef.current = false;
    };
  }, [enabled, ticketTypesKey, queryClient]);

  // Reconnect when token changes (e.g., after refresh)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'accessToken' && event.newValue) {
        console.log('[SSE] Token changed, will reconnect...');
        // Close existing connection to trigger reconnect
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
          isConnectingRef.current = false;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
}
